import { getLocalDateString } from '@core/dateUtils.js';
import { Calendar as FullCalendar } from '@fullcalendar/core';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';


export default () => ({
    calendar: null,


    init() {

        window.addEventListener('refetch-calendar', (e) => {
            this.refetchEvents();
        });

        const childId = this.$el.dataset.childId;
        this.calendar = this.createCalendar(this.$el, childId);
        this.shadowRoot = this.$el.getRootNode();

    },

    resize() {
        if (this.calendar) {
            this.calendar.updateSize();
        }
    },

    refetchEvents() {
        if (this.calendar) {
            this.calendar.refetchEvents();
        }
    },

    /**
     * Opens the details modal via HTMX
     */
    openDetailsModal(activityId, placementId, childId) {
        const btnDetails = this.shadowRoot.querySelector('#btnOpenDetailsModal');

        const url = `${window.NEXT_PORTAL_URL}/activity/details-modal`;

        btnDetails.setAttribute('hx-post', url);
        btnDetails.setAttribute('hx-vals', JSON.stringify({
            activityId: activityId,
            placementId: placementId,
            childId: childId
        }));

        htmx.process(btnDetails);
        btnDetails.click();
    },

    /**
     * Opens the activity selection modal via HTMX
     */
    openSelectionModal(childId, startDate = null, endDate = null) {
        const btnRegister = this.shadowRoot.querySelector('#openSelectionForChildWithDates');
        if (btnRegister) {
            // If dates are provided, pass them as parameters
            if (startDate && endDate) {
                const url = `${window.NEXT_PORTAL_URL}/activity/main-modal?childId=${childId}&startDate=${startDate}&endDate=${endDate}`;

                btnRegister.setAttribute('hx-get', url);
                htmx.process(btnRegister);
                btnRegister.click();
            }
        }
    },

    createCalendar(calendarEl, childId) {
        if (!calendarEl) return null;

        // Reference to the Alpine component for use in callbacks
        const self = this;

        // Utility function to check if a date is in the future
        const isFutureDate = (dateStr) => {
            const date = new Date(dateStr);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            return date >= today;
        };

        const calendar = new FullCalendar(calendarEl, {
            plugins: [dayGridPlugin, interactionPlugin],
            initialView: 'dayGridMonth',
            locale: 'ch-fr',
            headerToolbar: {
                left: 'prev,next today',
                center: '',
                right: 'title'
            },
            buttonText: {
                today: getMessage('kibe.calendar.button.today'),
                month: getMessage('kibe.calendar.button.month'),
                week: getMessage('kibe.calendar.button.week'),
                day: getMessage('kibe.calendar.button.day')
            },
            firstDay: 1,
            editable: false,
            selectable: true,
            selectConstraint: {
                start: new Date().toISOString().split('T')[0], // Today
                end: '2099-12-31' // Far future date
            },
            selectAllow: function(selectInfo) {
                return isFutureDate(selectInfo.start.toISOString().split('T')[0]);
            },
            dayMaxEvents: false,
            height: 'auto',
            weekends: false,
            eventOrder: 'order,title',

            events: function (info, successCallback, failureCallback) {
                const startDate = getLocalDateString(info.start);
                const endDate = getLocalDateString(info.end);
                const url = `${window.NEXT_PORTAL_URL}/activity/events?childId=${childId}&start=${startDate}&end=${endDate}`;

                fetch(url)
                    .then(response => {
                        if (!response.ok) {
                            throw new Error(`HTTP error: ${response.status}`);
                        }
                        return response.json();
                    })
                    .then(data => {
                        const events = data.map(event => {
                            const startTime = event.startTime || '00:00:00';
                            const endTime = event.endTime || '23:59:59';

                            const calEvent = {
                                id: event.id,
                                title: event.title,
                                start: event.allDay ? event.date : `${event.date}T${startTime}`,
                                end: event.allDay ? event.date : `${event.date}T${endTime}`,
                                allDay: event.allDay,
                                display: event.display,
                                className: event.className,
                                editable: false,
                                extendedProps: {
                                    eventType: event.eventType,
                                    startTime: event.startTime,
                                    endTime: event.endTime,
                                    activityId: event.actId
                                }
                            };

                            if (event.display && event.eventType === 'special-day') {
                                calEvent.start = event.date;
                                calEvent.allDay = true;
                                calEvent.display = 'background';
                                calEvent.className = ['special-day-event', event.className];
                            }

                            return calEvent;
                        });

                        successCallback(events);
                    })
                    .catch(error => {
                        console.error(getMessage('portal.activity.calendar.error.loading_events'), error);
                        failureCallback(error);
                    });
            },

            eventContent: function (arg) {
                const title = arg.event.title;
                const classNames = arg.event.classNames;

                if (arg.event.extendedProps?.eventType === 'activity') {
                    let icon = '<i class="fas fa-calendar-check ms-1"></i>';

                    if (classNames.includes('calendar-event-activity-warning')) {
                        icon = '<i class="fas fa-bell ms-1"></i>';
                    } else if (classNames.includes('calendar-event-activity-danger')) {
                        icon = '<i class="fas fa-bell ms-1"></i>';
                    }

                    return { html: `<div>${icon} ${title}</div>` };
                }

                return { html: `<div>${title}</div>` };
            },

            eventClick: function (info) {
                info.jsEvent.preventDefault();
                info.jsEvent.stopPropagation();

                const { eventType, activityId } = info.event.extendedProps;

                if (eventType === 'activity') {
                    const placementId = info.event.id;

                    self.openDetailsModal(activityId, placementId, childId);
                }
            },

            select: function(info) {
                // Check that we did not click on an event
                const clickedOnEvent = info.jsEvent?.target.closest('.fc-event');
                if (clickedOnEvent) {
                    calendar.unselect();
                    return;
                }

                // Check that the dates are in the future
                if (!isFutureDate(info.startStr)) {
                    calendar.unselect();
                    return;
                }

                const isHoliday = calendar.getEvents().some(event => {
                    if (event.extendedProps?.eventType !== 'special-day') return false;

                    const eventStartStr = event.startStr.split('T')[0];
                    const selStartStr = info.startStr.split('T')[0];

                    return eventStartStr === selStartStr;
                });

                if (isHolidayDate(calendar, info.startStr.split('T')[0])) {
                    calendar.unselect();
                    return;
                }

                const startDateStr = info.startStr;
                const endDate = new Date(info.end);
                endDate.setDate(endDate.getDate() - 1);
                const endDateStr = endDate.getFullYear() + '-' +
                    String(endDate.getMonth() + 1).padStart(2, '0') + '-' +
                    String(endDate.getDate()).padStart(2, '0');

                self.openSelectionModal(childId, startDateStr, endDateStr);

                // Deselect after opening
                calendar.unselect();
            }
        });

        calendar.render();
        return calendar;
    }
});

function isHolidayDate(calendar, dateStr) {
    return calendar.getEvents().some(event => {
        if (event.extendedProps?.eventType !== 'special-day') return false;
        return event.startStr.split('T')[0] === dateStr;
    });
}
